import { type ApiEndpoint } from './portal-types';

export const SampleLanguage = {
  Curl: 'curl',
  Node: 'node',
  Python: 'python',
  CSharp: 'csharp',
} as const;

export type SampleLanguage = (typeof SampleLanguage)[keyof typeof SampleLanguage];

export const SAMPLE_LANGUAGES: readonly { id: SampleLanguage; label: string }[] = [
  { id: SampleLanguage.Curl, label: 'cURL' },
  { id: SampleLanguage.Node, label: 'Node.js' },
  { id: SampleLanguage.Python, label: 'Python' },
  { id: SampleLanguage.CSharp, label: 'C#' },
];

export const DEFAULT_SAMPLE_LANGUAGE: SampleLanguage = SampleLanguage.Curl;

const API_BASE_URL = 'https://api.ctrlchain.com';

/**
 * Builds the request code sample for an endpoint in the given language.
 */
export function buildCodeSample(endpoint: ApiEndpoint, language: SampleLanguage): string {
  const url = API_BASE_URL + endpoint.examplePath;
  const body = endpoint.requestBody ? JSON.stringify(endpoint.requestBody, null, 2) : null;

  switch (language) {
    case SampleLanguage.Curl:
      return buildCurlSample(endpoint, url, body);
    case SampleLanguage.Node:
      return buildNodeSample(endpoint, url, body);
    case SampleLanguage.Python:
      return buildPythonSample(endpoint, url, body);
    case SampleLanguage.CSharp:
      return buildCSharpSample(endpoint, url, body);
  }
}

/**
 * Builds the formatted JSON response sample for an endpoint, or null when the endpoint returns a
 * file instead of a JSON body.
 */
export function buildResponseSample(endpoint: ApiEndpoint): string | null {
  if (!endpoint.response) {
    return null;
  }
  return JSON.stringify(endpoint.response, null, 2);
}

function buildCurlSample(endpoint: ApiEndpoint, url: string, body: string | null): string {
  const method = endpoint.method === 'GET' ? '' : `-X ${endpoint.method} `;
  let sample = `curl ${method}${url} \\\n  -H "apiKey: $CTRLCHAIN_API_KEY"`;
  if (body) {
    sample += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'`;
  }
  return sample;
}

function buildNodeSample(endpoint: ApiEndpoint, url: string, body: string | null): string {
  const lines = [
    `const res = await fetch("${url}", {`,
    `  method: "${endpoint.method}",`,
    '  headers: {',
    '    "apiKey": process.env.CTRLCHAIN_API_KEY,',
    ...(body ? ['    "Content-Type": "application/json",'] : []),
    '  },',
    ...(body ? [`  body: JSON.stringify(${body.split('\n').join('\n  ')}),`] : []),
    '});',
    'const data = await res.json();',
  ];
  return lines.join('\n');
}

function buildPythonSample(endpoint: ApiEndpoint, url: string, body: string | null): string {
  const lines = [
    'import os, requests',
    '',
    `res = requests.${endpoint.method.toLowerCase()}(`,
    `    "${url}",`,
    `    headers={"apiKey": os.environ['CTRLCHAIN_API_KEY']},`,
    ...(body ? [`    json=${body.split('\n').join('\n    ')},`] : []),
    ')',
    'data = res.json()',
  ];
  return lines.join('\n');
}

function buildCSharpSample(endpoint: ApiEndpoint, url: string, body: string | null): string {
  const httpMethod =
    endpoint.method === 'GET' ? 'Get' : endpoint.method === 'POST' ? 'Post' : 'Patch';
  const lines = [
    'var req = new HttpRequestMessage(',
    `    HttpMethod.${httpMethod},`,
    `    "${url}");`,
    'req.Headers.Add("apiKey", apiKey);',
    ...(body
      ? ['req.Content = new StringContent(', '    json, Encoding.UTF8, "application/json");']
      : []),
    'var res = await client.SendAsync(req);',
    'var data = await res.Content.ReadAsStringAsync();',
  ];
  return lines.join('\n');
}
